<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-obsidian-500">
      {{ formatDate(period.startDate, DateTime.DATE_SHORT) }} –
      {{ formatDate(period.endDate, DateTime.DATE_SHORT) }}
    </p>

    <OverrideDefaultValueField
      v-slot="{ defaultInputBinds }"
      name="customTotalBasePrice"
      :label="t('totalBasePrice')"
      type="number"
      :default-value="period.totalBasePrice"
      :formatted-default-value="formatCurrency(period.totalBasePrice)"
      :initial-overridden-value="period.customTotalBasePrice ?? period.totalBasePrice"
    >
      <Input
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        v-bind="defaultInputBinds"
      />
    </OverrideDefaultValueField>

    <OverrideDefaultValueField
      v-slot="{ defaultInputBinds }"
      name="customInclusiveKm"
      :label="t('inclusiveKm')"
      type="number"
      :default-value="defaultInclusiveKm ?? 0"
      :formatted-default-value="`${defaultInclusiveKm ?? 0} km`"
      :initial-overridden-value="getPeriodInclusiveKm(period) ?? defaultInclusiveKm ?? 0"
    >
      <Input type="number" :min="0" adornment="km" v-bind="defaultInputBinds" />
    </OverrideDefaultValueField>

    <ExtrasForm
      :key="extrasFormKey"
      :price-calculation="pricing"
      :period-index="periodIndex"
      :disabled="areAllExtrasReadOnly"
      :disabled-extra-types="frozenExtraTypes"
      :show-title="true"
      @update:booked-extras="
        pendingBookedExtras = $event;
        bookedExtrasTouched = true;
      "
      @update:custom-booked-extras="pendingCatalogCustomExtras = $event"
    />

    <CustomExtrasFields :booking="booking" :disabled="areAllExtrasReadOnly" />

    <Checkbox v-model="applyToFollowing" :label="t('applyToFollowing')" />

    <div class="flex justify-end gap-2">
      <CVButton variant="default" :is-loading="isBusy" @click.prevent="onSubmit">
        {{ t('save') }}
      </CVButton>
      <CVButton variant="secondary" outline :disabled="isBusy" @click.prevent="emit('cancel')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useForm } from 'vee-validate';
import { formatDate } from '@/hooks/use-formatted-date';
import { formatCurrency } from '@/utils/format-numbers';
import {
  bookingStartTimezone,
  getPeriodInclusiveKm,
  isLongTermPricing,
  isStartedPricingPeriod,
} from '@/utils/pricing.utils';
import OverrideDefaultValueField from '@/components/OverrideDefaultValueField.vue';
import Input from '@/components/Input.vue';
import Checkbox from '@/components/Checkbox.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import ExtrasForm, { type BookedExtraSelection } from '@/components/extras/ExtrasForm.vue';
import CustomExtrasFields from '@/components/booking-forms/CustomExtrasFields.vue';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import { periodQuoteFormSchema } from '@/validation/price-calculation.schema';
import { areAllExtrasFrozen, EXTRA_TYPES_FROZEN_AFTER_HANDOVER } from './use-is-extra-type-frozen';
import {
  PriceRecalculationMode,
  VehicleExtraType,
  type BookingPricingDto,
  type CustomBookedExtraDto,
} from '@carvia/ros-client-types';

const props = defineProps<{
  booking: Booking;
  periodIndex: number;
}>();

const emit = defineEmits<{
  (e: 'saved'): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();
const { confirmAndSave, isBusy } = usePriceChangeConfirmation();

const pricing = computed(() => {
  if (!isLongTermPricing(props.booking.priceCalculation)) {
    throw new Error('Expected long-term pricing');
  }
  return props.booking.priceCalculation;
});
const period = computed(() => pricing.value.periods[props.periodIndex]);
const defaultInclusiveKm = computed(
  () =>
    (period.value as typeof period.value & { defaultInclusiveKm?: number | null })
      .defaultInclusiveKm ?? null,
);

const editableCustomExtras = (extras: CustomBookedExtraDto[] | null | undefined) =>
  (extras ?? []).filter((extra) => !extra.isAdditional);

const additionalCustomExtras = computed(() =>
  (period.value.customBookedExtras ?? []).filter((extra) => extra.isAdditional),
);

const { handleSubmit } = useForm({
  initialValues: {
    customTotalBasePrice: period.value.customTotalBasePrice ?? null,
    customInclusiveKm:
      (period.value as typeof period.value & { customInclusiveKm?: number | null })
        .customInclusiveKm ?? null,
    customBookedExtras: editableCustomExtras(period.value.customBookedExtras),
  },
  validationSchema: periodQuoteFormSchema,
});

const applyToFollowing = ref(true);
const extrasFormKey = ref(0);
const pendingBookedExtras = ref<BookedExtraSelection[]>([]);
const bookedExtrasTouched = ref(false);
const pendingCatalogCustomExtras = ref<CustomBookedExtraDto[] | undefined>(undefined);

const areAllExtrasReadOnly = computed(() => areAllExtrasFrozen(props.booking.status));
const periodHasStarted = computed(() =>
  isStartedPricingPeriod(period.value, new Date(), bookingStartTimezone(props.booking)),
);
const frozenExtraTypes = computed(() => {
  if (areAllExtrasReadOnly.value) {
    return Object.values(VehicleExtraType);
  }
  if (!periodHasStarted.value) {
    return [];
  }
  return EXTRA_TYPES_FROZEN_AFTER_HANDOVER;
});
const onSubmit = handleSubmit(async (formValues) => {
  const catalogCustom = (pendingCatalogCustomExtras.value ?? []).filter(
    (extra) => extra.customVehicleExtraType === VehicleExtraType.EXTRA_KM,
  );
  const customBookedExtras = [
    ...additionalCustomExtras.value,
    ...catalogCustom,
    ...(formValues.customBookedExtras ?? []),
  ];
  const pricingDto = {
    periodIndex: props.periodIndex,
    applyToFollowing: applyToFollowing.value,
    customTotalBasePrice: formValues.customTotalBasePrice ?? null,
    customInclusiveKm: formValues.customInclusiveKm ?? null,
    bookedExtras: bookedExtrasTouched.value ? pendingBookedExtras.value : undefined,
    customBookedExtras,
    priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
  } as BookingPricingDto;
  const { status } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto: { pricing: pricingDto },
  });
  if (status === 'saved') {
    emit('saved');
  }
});
</script>

<i18n lang="json">
{
  "en": {
    "totalBasePrice": "Car price (without extras)",
    "inclusiveKm": "Inclusive km",
    "applyToFollowing": "Apply to following periods",
    "save": "Save",
    "cancel": "Cancel"
  },
  "de": {
    "totalBasePrice": "Fahrzeugpreis (ohne Extras)",
    "inclusiveKm": "Inklusivkilometer",
    "applyToFollowing": "Auf folgende Zeiträume anwenden",
    "save": "Speichern",
    "cancel": "Abbrechen"
  },
  "es": {
    "totalBasePrice": "Precio del vehículo (sin extras)",
    "inclusiveKm": "Km incluidos",
    "applyToFollowing": "Aplicar a los periodos siguientes",
    "save": "Guardar",
    "cancel": "Cancelar"
  }
}
</i18n>
